import { useMemo, useRef } from 'react';
import { ChevronRight, MoreHorizontal, Paperclip, Trash2 } from 'lucide-react';
import {
  uploadBugCommentFile,
  type BugActivity,
  type BugAttachment,
  type BugComment,
  type BugReport,
  type BugReportPriority,
  type BugReportStatus,
} from '../../lib/bugReportsApi';
import {
  useAddBugComment,
  useBugReport,
  useBugTimeline,
  useDeleteBugComment,
  useDeleteBugReport,
} from '../../hooks/useBugReports';
import { RelativeTime } from '../ui/RelativeTime';
import { AttachmentImage } from '../ui/AttachmentImage';
import { useConfirm } from '../ui/ConfirmProvider';
import { Avatar, MeAvatar } from '../tasks/linear/Avatar';
import { CommentComposerBox } from '../tasks/linear/CommentComposerBox';
import { MarkdownView } from '../tasks/linear/MarkdownView';
import { Popover } from '../tasks/linear/Popover';
import { BugPriorityEditor, BugStatusEditor } from './bugInlineEditors';
import { BUG_PRIORITY_COLOR, BUG_STATUS_COLOR, formatDateTime, useBugLabels } from './bugVisuals';
import { useI18n } from '../../contexts/I18nContext';

/**
 * One reported bug, laid out as the task detail page is: a `max-w-3xl` main
 * column with the breadcrumb, title, description, comments and activity, and
 * the ~280px properties sidebar down the right.
 *
 * The properties are the task sidebar's rows, not a boxed form — label left,
 * value right, hover highlight across the row, and status/priority behind the
 * same icon-and-popover editors a ticket uses.
 */
export function BugReportDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useI18n();
  const { data: report, isLoading, isError } = useBugReport(id);

  if (isLoading) {
    return (
      <div className="px-5 py-6 text-sm text-gray-400 sm:px-8 lg:px-10">
        {t('bugReports.detail.loading')}
      </div>
    );
  }
  if (isError || !report) {
    return (
      <div className="px-5 py-6 text-sm text-red-600 sm:px-8 lg:px-10">
        {t('bugReports.detail.notFound')}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50 text-gray-800 lg:flex-row">
      <main className="min-w-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 lg:px-10">
        <DetailMain report={report} onBack={onBack} />
      </main>
      <BugSidebar report={report} />
    </div>
  );
}

function DetailMain({ report, onBack }: { report: BugReport; onBack: () => void }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const remove = useDeleteBugReport();
  const { data: timeline, isLoading } = useBugTimeline(report.id);
  const addComment = useAddBugComment(report.id);
  const deleteComment = useDeleteBugComment(report.id);

  // One request, split two ways — the same contract the task page uses.
  const { comments, changes } = useMemo(() => {
    const items = timeline ?? [];
    return {
      comments: items.flatMap((item) => (item.kind === 'comment' ? [item.data] : [])),
      changes: items.flatMap((item) => (item.kind === 'activity' ? [item.data] : [])),
    };
  }, [timeline]);

  async function handleDelete(close: () => void) {
    close();
    if (!(await confirm({ message: t('bugReports.deleteConfirm'), danger: true }))) return;
    await remove.mutateAsync(report.id);
    onBack();
  }

  return (
    <div className="group/row mx-auto max-w-3xl">
      {/* Breadcrumb: section > tag, with the actions menu trailing, exactly as
          the task page lays its header out. */}
      <nav className="flex items-center gap-1 text-[13px] text-gray-500">
        <button type="button" onClick={onBack} className="hover:text-gray-800">
          {t('settings.tab.bugReports')}
        </button>
        <ChevronRight size={14} className="text-gray-300" />
        <span className="font-mono text-gray-700">{report.identifier}</span>

        <div className="ml-auto flex items-center gap-1">
          <Popover
            width={180}
            align="end"
            trigger={({ open, toggle, ref }) => (
              <button
                type="button"
                ref={ref}
                onClick={toggle}
                aria-label={t('bugReports.menu')}
                className={`flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800 ${
                  open ? 'bg-gray-200 text-gray-800' : ''
                }`}
              >
                <MoreHorizontal size={16} />
              </button>
            )}
          >
            {(close) => (
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => void handleDelete(close)}
                  className="ui-menu-item text-red-500"
                >
                  <Trash2 size={14} /> {t('bugReports.delete')}
                </button>
              </div>
            )}
          </Popover>
        </div>
      </nav>

      {/* Title. Read-only, unlike a task's: the reporter wrote it and the point
          of the record is what they said, not what triage would rather it said. */}
      <h1 className="mt-4 text-[22px] font-semibold leading-tight text-gray-900">{report.title}</h1>

      {/* Properties live up here on phones; the sidebar is hidden below lg. */}
      <div className="mt-3 lg:hidden">
        <MobileBugProperties report={report} />
      </div>

      <div className="mt-2">
        <MarkdownView content={report.description} />
      </div>

      {report.attachments.length > 0 && (
        <section className="mt-6">
          <SectionHeading
            title={t('bugReports.detail.attachments')}
            count={report.attachments.length}
          />
          <AttachmentChips attachments={report.attachments} />
        </section>
      )}

      <section className="mt-8">
        <SectionHeading title={t('bugReports.detail.comments')} count={comments.length} />
        {isLoading ? (
          <p className="text-[13px] text-gray-400">{t('bugReports.detail.loading')}</p>
        ) : comments.length === 0 ? (
          <p className="text-[13px] text-gray-400">{t('bugReports.detail.noComments')}</p>
        ) : (
          <ul className="space-y-3">
            {comments.map((comment) => (
              <CommentRow
                key={comment.id}
                comment={comment}
                onDelete={(commentId) => deleteComment.mutate(commentId)}
              />
            ))}
          </ul>
        )}

        <div className="mt-5 flex gap-2.5">
          <div className="hidden sm:block">
            <MeAvatar size={26} />
          </div>
          <CommentComposerBox
            uploadFile={(file, onProgress) => uploadBugCommentFile(report.id, file, onProgress)}
            placeholder={t('bugReports.detail.commentPlaceholder')}
            submitLabel={t('bugReports.detail.comment')}
            busy={addComment.isPending}
            onSubmit={(content, attachments) =>
              addComment.mutateAsync({
                content,
                attachments: attachments.length ? attachments : undefined,
              })
            }
          />
        </div>
      </section>

      <section className="mt-8 border-t border-gray-200 pt-6">
        <SectionHeading title={t('bugReports.detail.activity')} count={changes.length} />
        {changes.length === 0 ? (
          <p className="text-[13px] text-gray-400">{t('bugReports.detail.noActivity')}</p>
        ) : (
          <ul className="space-y-3">
            {changes.map((activity) => (
              <ActivityRow key={activity.id} activity={activity} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * One properties row: label left, value right, full-width hover highlight —
 * copied from the task sidebar so the two pages line up pixel for pixel.
 */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="-mx-2 flex min-h-[34px] items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-white/[0.03]">
      <span className="w-[88px] shrink-0 text-[12px] text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 items-center justify-start">{children}</div>
    </div>
  );
}

/**
 * Forward a click anywhere in the value area to the editor's own trigger — the
 * icon glyph alone is a small target and easy to miss.
 */
function ClickToEdit({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest('button')) return;
    ref.current?.querySelector('button')?.click();
  }

  return (
    <div ref={ref} onClick={handleClick} className="flex flex-1 cursor-pointer items-center gap-2">
      {children}
    </div>
  );
}

function StatusValue({ status }: { status: BugReportStatus }) {
  const labels = useBugLabels();
  return (
    <span className="text-[13px]" style={{ color: BUG_STATUS_COLOR[status] }}>
      {labels.status(status)}
    </span>
  );
}

function PriorityValue({ priority }: { priority: BugReportPriority }) {
  const labels = useBugLabels();
  return (
    <span className="text-[13px]" style={{ color: BUG_PRIORITY_COLOR[priority] }}>
      {labels.priority(priority)}
    </span>
  );
}

function BugSidebar({ report }: { report: BugReport }) {
  const { t } = useI18n();

  return (
    <aside className="hidden w-[280px] shrink-0 border-l border-gray-200 px-4 py-4 lg:block">
      <h2 className="mb-2 px-0 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {t('bugReports.props.title')}
      </h2>

      <Row label={t('bugReports.column.status')}>
        <ClickToEdit>
          <BugStatusEditor report={report} />
          <StatusValue status={report.status} />
        </ClickToEdit>
      </Row>

      <Row label={t('bugReports.column.priority')}>
        <ClickToEdit>
          <BugPriorityEditor report={report} />
          <PriorityValue priority={report.priority} />
        </ClickToEdit>
      </Row>

      <Row label={t('bugReports.props.reporter')}>
        <span className="truncate text-[13px] text-gray-700">
          {report.reporter?.name ?? t('bugReports.unknownReporter')}
        </span>
      </Row>

      <Row label={t('bugReports.props.createdAt')}>
        <span className="text-[13px] text-gray-700">{formatDateTime(report.createdAt)}</span>
      </Row>

      {report.resolvedAt && (
        <Row label={t('bugReports.props.resolvedAt')}>
          <span className="text-[13px] text-gray-700">{formatDateTime(report.resolvedAt)}</span>
        </Row>
      )}

      <Row label={t('bugReports.props.page')}>
        {/* Truncated, like every other value in this column: a deep URL with
            query params is longer than 280px and would otherwise wrap into
            four lines and shove the rows below it out of line. */}
        <span
          className="truncate font-mono text-[12px] text-gray-700"
          title={report.pageUrl ?? undefined}
        >
          {report.pageUrl ?? '—'}
        </span>
      </Row>

      <Row label={t('bugReports.props.viewport')}>
        <span className="text-[13px] text-gray-700">
          {report.viewportWidth && report.viewportHeight
            ? `${report.viewportWidth}×${report.viewportHeight}`
            : '—'}
        </span>
      </Row>

      <Row label={t('bugReports.props.browser')}>
        {/* The full user-agent is four lines of noise in a 280px column; the
            browser and platform are what triage actually reads, and the whole
            string stays one hover away. */}
        <span className="truncate text-[13px] text-gray-700" title={report.userAgent ?? undefined}>
          {shortUserAgent(report.userAgent)}
        </span>
      </Row>

      {report.appVersion && (
        <Row label={t('bugReports.props.appVersion')}>
          <span className="text-[13px] text-gray-700">{report.appVersion}</span>
        </Row>
      )}
    </aside>
  );
}

/** Phone-sized replacement for the sidebar: the two editable properties as chips. */
function MobileBugProperties({ report }: { report: BugReport }) {
  return (
    <div className="touch-target-auto flex flex-wrap items-center gap-1.5">
      <Chip>
        <BugStatusEditor report={report} />
        <StatusValue status={report.status} />
      </Chip>
      <Chip>
        <BugPriorityEditor report={report} />
        <PriorityValue priority={report.priority} />
      </Chip>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest('button')) return;
    ref.current?.querySelector('button')?.click();
  }

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className="inline-flex min-h-[30px] cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-200"
    >
      {children}
    </div>
  );
}

/**
 * Turn a user-agent into the two facts triage needs — browser and platform.
 * Falls back to the raw string when it does not parse.
 */
export function shortUserAgent(ua: string | null): string {
  if (!ua) return '—';

  const browser =
    /Edg\/([\d.]+)/.exec(ua)?.[0].replace('Edg/', 'Edge ') ??
    /OPR\/([\d.]+)/.exec(ua)?.[0].replace('OPR/', 'Opera ') ??
    /Firefox\/([\d.]+)/.exec(ua)?.[0].replace('/', ' ') ??
    /Chrome\/([\d.]+)/.exec(ua)?.[0].replace('/', ' ') ??
    (/Safari\//.test(ua) ? `Safari ${/Version\/([\d.]+)/.exec(ua)?.[1] ?? ''}`.trim() : null);

  const platform = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Macintosh|Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;

  // Keep the major version only: "Chrome 152.0.0.0" says nothing "Chrome 152"
  // does not, and the column is 280px wide.
  const trimmed = browser?.replace(/ ([\d]+)[\d.]*$/, ' $1');
  if (trimmed && platform) return `${trimmed} · ${platform}`;
  return trimmed ?? platform ?? ua;
}

function SectionHeading({ title, count }: { title: string; count: number }) {
  return (
    <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-gray-500">
      {title}
      {count > 0 && (
        <span className="rounded-full bg-gray-200 px-1.5 py-px text-[11px] font-medium text-gray-500">
          {count}
        </span>
      )}
    </h2>
  );
}

function isImage(attachment: BugAttachment): boolean {
  return attachment.contentType.startsWith('image/');
}

/**
 * `download` keeps the original name, diacritics included: the storage key was
 * sanitised at upload, so the server can only offer the mangled one.
 */
function FileChip({ attachment }: { attachment: BugAttachment }) {
  return (
    <a
      href={attachment.url}
      download={attachment.name}
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded border border-gray-200 px-2 py-1 text-[12px] text-gray-500 hover:text-gray-800"
    >
      <Paperclip size={12} /> {attachment.name}
    </a>
  );
}

function AttachmentChips({ attachments }: { attachments: BugAttachment[] }) {
  if (attachments.length === 0) return null;
  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));
  return (
    <div className="space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((attachment, index) => (
            <AttachmentImage
              key={`${attachment.name}-${index}`}
              src={attachment.url}
              alt={attachment.name}
              className="max-h-48 rounded-md border border-gray-200 object-cover"
              placeholderClassName="h-48 w-64"
              fallback={<FileChip attachment={attachment} />}
            />
          ))}
        </div>
      )}
      {files.map((attachment, index) => (
        <FileChip key={`${attachment.name}-${index}`} attachment={attachment} />
      ))}
    </div>
  );
}

function CommentRow({
  comment,
  onDelete,
}: {
  comment: BugComment;
  onDelete: (id: string) => void;
}) {
  const { t } = useI18n();
  return (
    <li className="flex gap-2.5">
      <Avatar name={comment.author?.name ?? null} size={26} />
      <div className="min-w-0 flex-1 rounded-md border border-gray-200 bg-card px-3 py-2">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-medium text-gray-800">
            {comment.author?.name ?? t('bugReports.unknownReporter')}
          </span>
          <div className="flex items-center gap-2">
            <RelativeTime iso={comment.createdAt} className="text-[11px] text-gray-400" />
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="text-gray-400 hover:text-red-500"
              aria-label={t('bugReports.deleteComment')}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
        {comment.content && <MarkdownView content={comment.content} />}
        <div className="mt-2">
          <AttachmentChips attachments={comment.attachments} />
        </div>
      </div>
    </li>
  );
}

/** One line of change history: who changed what, from what, to what, when. */
function ActivityRow({ activity }: { activity: BugActivity }) {
  const { t } = useI18n();
  const labels = useBugLabels();

  function readable(field: string, value: string | null): string {
    if (!value) return '—';
    if (field === 'status') return labels.status(value as BugReportStatus);
    if (field === 'priority') return labels.priority(value as BugReportPriority);
    return value;
  }

  const text =
    activity.field === 'created'
      ? t('bugReports.activity.created')
      : t('bugReports.activity.changed', {
          field: t(`bugReports.activity.field.${activity.field}`),
          from: readable(activity.field, activity.oldValue),
          to: readable(activity.field, activity.newValue),
        });

  return (
    <li className="flex gap-2 pl-1 text-[12.5px] text-gray-400">
      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
      <div className="min-w-0 flex-1">
        <span>
          {activity.user?.name ?? '—'} {text}
        </span>
        <span className="text-gray-300"> · </span>
        <RelativeTime iso={activity.createdAt} />
      </div>
    </li>
  );
}
